/**
 * Getting the wounded home (M4-06).
 *
 * The most complete scenario the MVP has: somebody is down three hundred tiles
 * from the village in the rain, and the party has to hold off the wolves,
 * staunch the bleeding, and *move them*. This module is the moving.
 *
 * Three ways to do it, and the ticket's numbers behind each (`BURDEN_SPEED`):
 *
 * - **Drag** — one hand, ×0.4, and the body bumps along behind you over
 *   whatever the ground is made of. Rough ground rolls against the patient's
 *   healing wounds, so dragging a man with a gut wound is triage arithmetic
 *   rather than obviously right.
 * - **Carry** — over the shoulder, ×0.6, both arms full, and **you cannot
 *   fight**. Dropping them is instant and free, because a carrier who has to
 *   spend an action to defend themselves is a carrier who dies holding somebody.
 * - **Litter** — two people, ×0.8, and *no strain rolls at all*. The good
 *   answer, and it needs three players: two carrying and somebody watching the
 *   treeline. That requirement is the co-op design, not a limitation of it.
 *
 * ## Where the movement actually happens
 *
 * Not here, and not in `movement.ts` either — in a **system**. `burdenSystem`
 * runs after the intent phase and glues the burden to whoever has hold of it:
 * a carried body sits on the carrier's tile, a dragged one on the tile behind
 * them. That is deliberate, and it is the same argument `layTracks` makes from
 * inside the move handler: every future way of moving somebody — a step, a
 * knockback, a teleport nobody has written yet — drags the body along without
 * anyone having remembered to add a call, because the system asks where the
 * carrier *is* rather than watching for them to move.
 *
 * The cost of moving while burdened *is* in `movement.ts`, because that is
 * where a step's cost is decided; what it reads is `burdenSpeed`, which lives in
 * `fighter.ts` with the state. No rule is in two places.
 */
import { capabilityFault } from './affordance.js';
import { chebyshev, DIRECTION_VECTORS } from './actor.js';
import { parseCarry, parseDrag, parseRelease } from './actions.js';
import { deriveCapabilities } from './body.js';
import { isHelpless, kitOf } from './fighter.js';
import { moveCostAt } from './map.js';
import { canStand, layTracksFor } from './movement.js';
import { strainOnExertion } from './recovery.js';
/* -------------------------------------------------------------------------- *
 * Balance
 * -------------------------------------------------------------------------- */
/**
 * Mobility a carrier needs to get somebody onto their shoulder and stay upright.
 *
 * The ticket's 0.6, and it is the one capability gate that is about legs rather
 * than hands: a man who can barely walk cannot carry anybody, however good his
 * grip is, and the honest consequence is that two wounded people cannot rescue
 * each other. Dragging has no such floor — you can drag on your knees.
 */
export const CARRY_MOBILITY = 0.6;
/**
 * How rough the ground has to be before a dragged body is being hurt by it.
 *
 * Read off the terrain's own move cost (M3-01), which is already the game's
 * measure of how bad the going is: bog, scree and thicket are above it and
 * meadow, track and grass are below. One number, and no second table of "rough"
 * tiles that would drift out of step with the first the day somebody adds a
 * tile kind.
 */
export const ROUGH_GROUND = 1.4;
/**
 * Resolve whatever a burden names.
 *
 * One function for three tables, because *hauling is one act*: the weight of a
 * dead man and a live one are the same weight, and a rescue and a burial and
 * dragging a stag home differ in what it means rather than in what it costs.
 */
export function haulableAt(world, id) {
    const actor = world.actors[id];
    if (actor) {
        return {
            id,
            name: actor.name,
            get x() {
                return actor.x;
            },
            set x(value) {
                actor.x = value;
            },
            get y() {
                return actor.y;
            },
            set y(value) {
                actor.y = value;
            },
            combatant: world.combat[id] ?? null,
        };
    }
    const corpse = world.corpses[id];
    if (corpse) {
        return {
            id,
            name: `${corpse.name}'s body`,
            get x() {
                return corpse.x;
            },
            set x(value) {
                corpse.x = value;
            },
            get y() {
                return corpse.y;
            },
            set y(value) {
                corpse.y = value;
            },
            combatant: null,
        };
    }
    const carcass = world.carcasses[id];
    if (carcass) {
        return {
            id,
            name: 'the carcass',
            get x() {
                return carcass.x;
            },
            set x(value) {
                carcass.x = value;
            },
            get y() {
                return carcass.y;
            },
            set y(value) {
                carcass.y = value;
            },
            combatant: null,
        };
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * The gate
 * -------------------------------------------------------------------------- */
/**
 * Why this body cannot be picked up, or `null`.
 *
 * Shares the reach rule with `helplessFault` and parts company on *what* may be
 * taken hold of: the helpless verbs work on people, and this works on anything
 * that has stopped moving under its own power — which is the same list plus the
 * dead and the butcherable.
 */
export function burdenFault(world, catalogue, actor, targetId, tick, mode) {
    if (targetId === actor.id)
        return 'you cannot carry yourself';
    const target = haulableAt(world, targetId);
    if (!target)
        return 'there is nothing there to take hold of';
    if (chebyshev(actor.x, actor.y, target.x, target.y) > 1) {
        return `${target.name} is out of reach`;
    }
    const carrier = world.combat[actor.id];
    if (!carrier || isHelpless(catalogue, carrier, tick))
        return 'you are in no state to';
    // A body that can stop you is a body you are not carrying anywhere. Corpses
    // and carcasses have no say in it, which is the whole of the difference.
    if (target.combatant && !isHelpless(catalogue, target.combatant, tick)) {
        return `${target.name} is still on their feet`;
    }
    const capFault = capabilityFault(catalogue, carrier, mode === 'drag' ? 'drag' : 'carry');
    if (capFault)
        return capFault;
    if (mode !== 'drag') {
        const kit = kitOf(catalogue, carrier);
        const mobility = kit ? deriveCapabilities(kit.plan, carrier.vitals.body).mobility : 1;
        if (mobility < CARRY_MOBILITY)
            return 'your legs will not take the weight';
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * Taking hold
 * -------------------------------------------------------------------------- */
/**
 * Is somebody else already carrying this body?
 *
 * The litter, and it is emergent rather than declared: two people who both take
 * hold of the same patient *are* a litter, and both of them get the better
 * speed and the patient stops being strained. The ticket describes the litter as
 * a craftable object of a cloak and two spears; a satchel that cannot hold a
 * spear is the reason it is not one yet (weapon swapping does not make long
 * tools into craft ingredients, and a "litter" item nobody could assemble would be a promise rather than a
 * mechanic). What the acceptance criterion actually asks for — that the good
 * answer *requires being two players* — is exactly what this gives, and the
 * object can arrive with M5-01's trade without changing a rule here.
 */
function otherCarrier(world, targetId, exceptId) {
    for (const id of Object.keys(world.combat).sort()) {
        if (id === exceptId)
            continue;
        const other = world.combat[id];
        if (other?.burden?.targetId === targetId && other.burden.mode !== 'drag')
            return other;
    }
    return null;
}
function takeHold(ctx, actor, carrier, target, mode) {
    // A hand on a body is a hand off whatever else it was doing.
    carrier.channel = null;
    const partner = mode === 'drag' ? null : otherCarrier(ctx.world, target.id, actor.id);
    const shared = partner ? 'litter' : mode;
    const burden = { targetId: target.id, mode: shared, since: ctx.tick };
    carrier.burden = burden;
    if (partner)
        partner.burden = { ...burden };
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: [mode === 'drag' ? 'drag' : 'carry', 'taken', shared],
        text: shared === 'litter'
            ? `${actor.name} takes ${target.name}'s feet, and the two of them lift together.`
            : mode === 'drag'
                ? `${actor.name} takes ${target.name} under the arms and heaves.`
                : `${actor.name} gets ${target.name} up onto their shoulder.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Letting go
 * -------------------------------------------------------------------------- */
/**
 * Put them down — or drop them.
 *
 * Free and instant, because the alternative gets carriers killed. What it costs
 * is the patient: a body put down on scree is a body that has been put down on
 * scree, and the strain roll says so. On meadow it costs nothing, which is the
 * decision the verb is really offering — *where* you drop them.
 */
export function release(ctx, actor, carrier) {
    const burden = carrier.burden;
    if (!burden)
        return;
    carrier.burden = null;
    const target = haulableAt(ctx.world, burden.targetId);
    if (!target)
        return;
    // A litter with one end let go is somebody else's carry now, not a litter.
    demote(ctx.world, burden.targetId);
    const rough = moveCostAt(ctx.world.map, target.x, target.y) >= ROUGH_GROUND;
    const patient = ctx.world.actors[burden.targetId];
    if (rough && patient && target.combatant)
        strainOnExertion(ctx, patient);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, burden.targetId],
        refs: ['release', rough ? 'hard-ground' : 'set-down'],
        text: rough
            ? `${actor.name} puts ${target.name} down hard.`
            : `${actor.name} sets ${target.name} down.`,
        audience: 'witnesses',
        at: { x: target.x, y: target.y },
    });
}
/** Whoever is left holding this body alone is carrying it, not littering it. */
function demote(world, targetId) {
    const holders = Object.keys(world.combat)
        .sort()
        .filter((id) => world.combat[id]?.burden?.targetId === targetId);
    if (holders.length !== 1)
        return;
    const alone = world.combat[holders[0]];
    if (alone?.burden?.mode === 'litter') {
        alone.burden = { ...alone.burden, mode: 'carry' };
    }
}
/* -------------------------------------------------------------------------- *
 * The system
 * -------------------------------------------------------------------------- */
/**
 * Keep every burden where its carrier is.
 *
 * Runs after the intent phase, so a carrier who walked this tick has already
 * walked. A carried body is *on* the carrier's tile — two people on one square,
 * which is exactly what a fireman's lift is — and a dragged one is on the tile
 * behind them, or on their tile when there is nowhere behind to put it.
 *
 * Three things fall out of doing it here rather than in the move handler, and
 * all three are the reason:
 *
 * 1. the patient's own clocks and their **blood trail** are laid as they move,
 *    so a bleeding man leaves a line through the bracken that draws the wolves
 *    down on the group carrying him (M3-08's sign, M2-05's noses);
 * 2. a burden whose carrier died, went down, or walked out of the world is
 *    dropped by the same pass that would have moved it, so there is no state
 *    left pointing at a body nobody is holding;
 * 3. every future way of moving an actor pulls the burden along for free.
 */
export const burdenSystem = {
    name: 'burdens',
    phase: 'sim',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const carrier = ctx.world.combat[id];
            const burden = carrier?.burden;
            const actor = ctx.world.actors[id];
            if (!carrier || !burden)
                continue;
            // The carrier is gone, out cold, or dead: hands open.
            if (!actor || isHelpless(ctx.catalogue, carrier, ctx.tick)) {
                carrier.burden = null;
                demote(ctx.world, burden.targetId);
                continue;
            }
            const target = haulableAt(ctx.world, burden.targetId);
            if (!target) {
                carrier.burden = null;
                continue;
            }
            // A patient who woke up is not cargo. Being carried by a friend is not
            // something the game should make you fight your way out of, so this is a
            // release rather than a refusal — and the message says who let go.
            if (target.combatant && !isHelpless(ctx.catalogue, target.combatant, ctx.tick)) {
                carrier.burden = null;
                demote(ctx.world, burden.targetId);
                ctx.emit({
                    type: 'interacted',
                    actors: [id, burden.targetId],
                    refs: ['release', 'woke'],
                    text: `${target.name} comes to, and gets their own feet under them.`,
                    audience: 'witnesses',
                    at: { x: target.x, y: target.y },
                });
                continue;
            }
            const [wantX, wantY] = restingPlace(ctx, actor, burden.mode, burden.targetId);
            if (target.x === wantX && target.y === wantY)
                continue;
            const direction = headingOf(target.x, target.y, wantX, wantY);
            target.x = wantX;
            target.y = wantY;
            // What the patient leaves on the ground on the way home (M3-08). The
            // blood trail is the tension of the whole march: it is the thing that
            // makes the direct route the right one.
            if (direction && ctx.world.actors[burden.targetId]) {
                layTracksFor(ctx, ctx.world.actors[burden.targetId], direction);
            }
            // Dragging over bad ground works at the wounds (M4-04's strain). A litter
            // never rolls, which is what it is for; a carry does not either, because
            // the jolting is the carrier's problem and it is already costing them
            // their hands and their wind.
            if (burden.mode === 'drag' && target.combatant) {
                const patient = ctx.world.actors[burden.targetId];
                if (patient && moveCostAt(ctx.world.map, target.x, target.y) >= ROUGH_GROUND) {
                    strainOnExertion(ctx, patient);
                }
            }
        }
    },
};
/** Where a burden should be sitting, given who has hold of it and how. */
function restingPlace(ctx, carrier, mode, targetId) {
    if (mode !== 'drag')
        return [carrier.x, carrier.y];
    // Behind, by the way the carrier is facing — the body bumping along in the
    // furrow it is making. Onto the carrier's own tile when there is no room,
    // which is the honest answer in a doorway.
    //
    // The body itself does not count as somebody standing in the way: it is
    // being dragged off the tile it is on, exactly as M2-07's shove was.
    const [dx, dy] = DIRECTION_VECTORS[carrier.facing];
    const x = carrier.x - dx;
    const y = carrier.y - dy;
    return canStand(ctx.world, x, y, targetId) ? [x, y] : [carrier.x, carrier.y];
}
/** The heading a burden just travelled, for the sign it leaves. */
function headingOf(fromX, fromY, toX, toY) {
    const dx = Math.sign(toX - fromX);
    const dy = Math.sign(toY - fromY);
    for (const [direction, vector] of Object.entries(DIRECTION_VECTORS)) {
        if (vector[0] === dx && vector[1] === dy)
            return direction;
    }
    return null;
}
/**
 * Why there is nothing to put down, or `null` (M4-06).
 *
 * A `ground`-shaped fault so the verb registry can offer "put down" in the menu
 * beside the other things you do to yourself — it takes no target, because you
 * are holding one thing or none.
 */
export function releaseFault(world, _catalogue, actor) {
    return world.combat[actor.id]?.burden ? null : 'you are not carrying anybody';
}
/* -------------------------------------------------------------------------- *
 * Handlers
 * -------------------------------------------------------------------------- */
function holdHandler(type, parse, mode) {
    return {
        type,
        parse,
        check(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            if (!actor)
                return `${intent.actorId} is not in the world`;
            return burdenFault(ctx.world, ctx.catalogue, actor, intent.payload.targetId, ctx.tick, mode);
        },
        apply(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            const carrier = ctx.world.combat[intent.actorId];
            const target = haulableAt(ctx.world, intent.payload.targetId);
            if (!actor || !carrier || !target)
                return;
            takeHold(ctx, actor, carrier, target, mode);
        },
    };
}
export const dragHandler = holdHandler('drag', parseDrag, 'drag');
export const carryHandler = holdHandler('carry', parseCarry, 'carry');
/**
 * Let go.
 *
 * Never refused, whatever state anybody is in. A verb whose whole job is to
 * *stop* doing something must not be gated on being well enough to do it.
 */
export const releaseHandler = {
    type: 'release',
    parse: parseRelease,
    check(intent, ctx) {
        const carrier = ctx.world.combat[intent.actorId];
        if (!carrier?.burden)
            return 'you are not carrying anybody';
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const carrier = ctx.world.combat[intent.actorId];
        if (!actor || !carrier)
            return;
        release(ctx, actor, carrier);
    },
};
export const CARRY_INTENTS = [
    dragHandler,
    carryHandler,
    releaseHandler,
];
//# sourceMappingURL=carry.js.map